import * as React from 'react';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import type {SmtpConnectionVerificationStatus} from 'nutshell-graphql-types';
import type {UserEmail, SmtpSettingsUpdateInputFieldInput} from 'nutshell-graphql-types';

import {SubmitSmtpSettings} from './submit-smtp-settings';

type Props = {
    smtpSettings?: SmtpSettingsUpdateInputFieldInput;
    children: (arg1: {
        updateAndTest: (newSettings?: SmtpSettingsUpdateInputFieldInput) => Promise<any>;
        inProgress: boolean;
    }) => React.ReactElement;
    onVerificationFailure: (arg1: UserEmail) => void;
    onVerificationSuccess: (arg1: UserEmail) => void;
    onError: (errorMessage: string) => void;
    onVerificationTimeout: () => void;
};

export function SubmitSmtp(props: Props) {
    const [isSubmitting, setSubmitting] = React.useState<boolean>(false);
    const [submittedEmailId, setSubmittedEmailId] = React.useState<string | null | undefined>(null);

    function handlePollingTimeout() {
        setSubmitting(false);
        props.onVerificationTimeout();
    }

    return (
        <SubmitSmtpSettings onVerificationPollTimeout={handlePollingTimeout}>
            {({
                isSubmittingSettings,
                isVerifying,
                attemptToVerifySmtpConnection,
                updateSmtpSettings,
                configuredUserEmails,
                refetchEmails,
            }) => {
                const userEmail = findSubmittedEmail(configuredUserEmails, submittedEmailId);
                const isMutationInFlight = isSubmittingSettings || isVerifying;
                if (userEmail) {
                    const status = getVerificationStatus(userEmail);
                    if (status === 'VERIFIED') {
                        props.onVerificationSuccess(userEmail);
                    }

                    // Check that we're not updating or verifying, because if we are, then the userEmail will be stale.
                    else if (status === 'FAILED' && !isMutationInFlight) {
                        props.onVerificationFailure(userEmail);
                    }
                }

                if (isMutationInFlight && !isSubmitting) {
                    setSubmitting(true);
                }

                function updateAndTest(newSettings?: SmtpSettingsUpdateInputFieldInput) {
                    const smtpSettings = newSettings || props.smtpSettings;

                    if (!smtpSettings) {
                        props.onError('No new settings to save');

                        return Promise.reject('No new settings to save');
                    }

                    setSubmittedEmailId(smtpSettings.userEmailId);

                    return (
                        updateSmtpSettings({
                            variables: {smtpSettings},
                        })
                            // @ts-expect-error
                            .catch((err) => {
                                const errorMessage = safelyGetGraphQLErrorMessage(err);
                                if (errorMessage) props.onError(errorMessage);
                            })
                            .then(() => {
                                attemptToVerifySmtpConnection({
                                    variables: {
                                        userEmailId: smtpSettings.userEmailId,
                                    },
                                })
                                    .then(() => {
                                        refetchEmails();
                                        setSubmitting(false);
                                    })
                                    // @ts-expect-error
                                    .catch((err) => {
                                        const errorMessage = safelyGetGraphQLErrorMessage(err);
                                        if (errorMessage) props.onError(errorMessage);
                                    });
                            })
                    );
                }

                return props.children({
                    updateAndTest,
                    inProgress: isSubmitting,
                });
            }}
        </SubmitSmtpSettings>
    );
}

// @ts-expect-error
function findSubmittedEmail(userEmails, emailIdToFind): UserEmail | null | undefined {
    // @ts-expect-error
    return userEmails.find((userEmail) => userEmail.id === emailIdToFind);
}

function getVerificationStatus(
    userEmail?: UserEmail | null
): SmtpConnectionVerificationStatus | null | undefined {
    return userEmail && userEmail.smtpCredentials && userEmail.smtpCredentials.verificationStatus;
}
